import "core-js/modules/es.array.push.js";
const __default__ = {
  head: {
    title: function () {
      return {
        inner: this.pageTitle
      };
    },
    meta: []
  }
};
import { ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { useStore } from "vue2-helpers/vuex";
import { useFileDialog } from "@vueuse/core";
import LoadingMessage from "@/components/common/LoadingMessage.vue";
import ConfirmSlotModal from "@/components/common/ConfirmSlotModal.vue";
import MessageModal from "@/components/common/MessageModal.vue";
import RowItem from "@/components/RowItem.vue";
import axios from "axios";
import { getData, createData } from "@/services/axios";
import { useMessageUrlValidator } from "@/composables/messageUrlValidator";
export default /*#__PURE__*/Object.assign(__default__, {
  __name: 'ForumEdit',

  setup(__props, {
    expose
  }) {
    // @ts-check
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const isLoading = ref(false);
    const loadMessage = "";
    const isCreate = computed(() => !route.params.id);
    const pageTitle = computed(() => isCreate.value ? "記事作成" : "記事編集");
    const title = ref("");
    const message = ref("");
    const s3AttachedPath = ref("");
    const fileName = ref("");
    const {
      isUrlValidate
    } = useMessageUrlValidator(message);

    const setData = async () => {
      try {
        isLoading.value = true;
        const data = await getData(`school_forums/${route.params.id}`);
        const forumData = data.items;
        title.value = forumData.title;
        message.value = forumData.message ?? "";

        if (forumData.attachment[0]) {
          s3AttachedPath.value = forumData.attachment[0].object_key;
          fileName.value = forumData.attachment[0].file_name;
        }
      } catch (error) {
        console.log(error);
        isLoading.value = true;
      } finally {
        isLoading.value = false;
      }
    };

    const getS3UrlStatusRunning = ref(false);
    const getS3UrlStatus = ref("内容を確認");

    const getS3SignedUrl = async () => {
      getS3UrlStatus.value = "お待ち下さい";
      getS3UrlStatusRunning.value = true; //secure browserのpopup block 対策

      const newWindow = window.open();

      try {
        const data = await getData("reader/get_s3_signed_url?path=" + encodeURIComponent(s3AttachedPath.value));
        newWindow.location = data.items.s3url;
        getS3UrlStatus.value = "内容を確認";
        getS3UrlStatusRunning.value = false;
      } catch (error) {
        console.log(error);
      }
    };

    const error = ref(
    /** @type {Map<"title" | "message", string>} */
    new Map([["title", ""], ["message", ""]]));

    const validateMessage = () => {
      // エラーの状態をデフォルトに戻す
      error.value = new Map([["title", ""], ["message", ""]]);
      let valid = true;

      if (!title.value) {
        error.value.set("title", "掲示板のタイトルを入力してください。");
        valid = false;
      } else if (title.value.length > 100) {
        "掲示板のタイトルは100文字以内で入力してください。";
        error.value.set("title", "掲示板のタイトルは100文字以内で入力してください。");
        valid = false;
      }

      if (message.value.length > 3000) {
        error.value.set("message", "メッセージ本文は3000文字以内で入力してください。");
        valid = false;
      }

      return valid;
    };

    const errorTotalText = computed(() => {
      let text = "";
      error.value.forEach(value => {
        if (value) {
          text += `${value}\n`;
        }
      });
      return text;
    });
    /** @type {Ref<File | null>} */

    const attachment = ref(null);
    const attachmentError = ref("");
    const {
      open,
      reset,
      onChange
    } = useFileDialog({
      multiple: false
    });
    onChange(files => {
      if (checkFile(files[0])) {
        attachment.value = files[0];
        attachmentError.value = "";
      } else {
        reset();
      }
    });

    const checkFile = file => {
      //拡張子はpdf, jpg, jpeg, png, gif
      const fileName = file.name;

      if (!(fileName.endsWith(".pdf") || fileName.endsWith(".jpg") || fileName.endsWith(".jpeg") || fileName.endsWith(".png") || fileName.endsWith(".gif"))) {
        attachmentError.value = "ファイルの種類が不正です。添付できるファイルの種類はpdf, jpg, jpeg, png, gifです。";
        return false;
      } //サイズは5MBまで


      if (file.size > 5242880) {
        attachmentError.value = "ファイルサイズが5MBを超えています。添付できるファイルのサイズは5MB以下です。";
        return false;
      }

      return true;
    }; // input type=file 側の unset


    const unsetAttachment = () => {
      reset();
      attachment.value = null;
    };

    const isShowUnsetAttachmentConfirmModal = ref(false);

    const showUnsetAttachmentConfirmModal = () => {
      isShowUnsetAttachmentConfirmModal.value = true;
    };

    const hideUnsetAttachmentConfirmModal = () => {
      isShowUnsetAttachmentConfirmModal.value = false;
    }; // s3 パスの unset


    const unsetS3Attachment = () => {
      s3AttachedPath.value = "";
      hideUnsetAttachmentConfirmModal();
    };

    const isShowConfirmModal = ref(false);
    const isShowCompleteModal = ref(false);

    const checkMessageConfirm = () => {
      if (!validateMessage()) {
        return false;
      }

      isShowConfirmModal.value = true;
    };

    const hideConfirmModal = () => {
      isShowConfirmModal.value = false;
    };

    const createEditForum = async () => {
      /** @type {{ title: string; message: string; attachment?: File; file_name?: string }} */
      const payload = {
        title: title.value,
        message: message.value
      };

      if (attachment.value) {
        // 添付ファイル作成
        payload.attachment = attachment.value;
        payload.file_name = attachment.value.name;
      }

      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        formData.append(key, value);
      });

      try {
        isLoading.value = true;

        if (isCreate.value) {
          await createData("school_forums", formData);
          store.dispatch("commitModalMessage", {
            message: "記事の投稿が完了しました",
            title: "投稿完了"
          });
        } else {
          if (!s3AttachedPath.value && !attachment.value) {
            formData.append("delete", "true");
          } // 素のputではformdataが使えなかったのでmethodを指定しつつpostする


          formData.append("_method", "put");
          await createData(`school_forums/${route.params.id}`, formData);
          store.dispatch("commitModalMessage", {
            message: "記事を編集しました",
            title: "編集完了"
          });
        }

        isShowCompleteModal.value = true;
      } catch (error) {
        console.log(error);

        if (axios.isAxiosError(error) && error.response?.status === 406) {
          attachmentError.value = "ファイルの種類が不正です。添付できるファイルの種類はpdf, jpg, jpeg, png, gifです。";
        }
      } finally {
        isShowConfirmModal.value = false;
        isLoading.value = false;
      }
    };

    const completeCreate = () => {
      isShowCompleteModal.value = false;
      router.push({
        name: "forums"
      });
    }; // created


    if (!isCreate.value) {
      // 編集の時だけデータを取得する
      setData();
    }

    expose({
      pageTitle
    });
    return {
      __sfc: true,
      route,
      router,
      store,
      isLoading,
      loadMessage,
      isCreate,
      pageTitle,
      title,
      message,
      s3AttachedPath,
      fileName,
      isUrlValidate,
      setData,
      getS3UrlStatusRunning,
      getS3UrlStatus,
      getS3SignedUrl,
      error,
      validateMessage,
      errorTotalText,
      attachment,
      attachmentError,
      open,
      reset,
      onChange,
      checkFile,
      unsetAttachment,
      isShowUnsetAttachmentConfirmModal,
      showUnsetAttachmentConfirmModal,
      hideUnsetAttachmentConfirmModal,
      unsetS3Attachment,
      isShowConfirmModal,
      isShowCompleteModal,
      checkMessageConfirm,
      hideConfirmModal,
      createEditForum,
      completeCreate,
      LoadingMessage,
      ConfirmSlotModal,
      MessageModal,
      RowItem
    };
  }

});